// src/components/StripeCheckout.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import stripePromise, { calculateFees } from '../services/stripe';
import { auth, db } from '../services/firebase';
import { doc, addDoc, collection, updateDoc, serverTimestamp } from 'firebase/firestore';
import { COLORS, LAYOUT } from '../constants';

// Stripe Card Element styling to match ISU theme
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#333',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      '::placeholder': {
        color: '#aaa',
      },
      padding: '12px',
    },
    invalid: {
      color: COLORS.primary,
      iconColor: COLORS.primary,
    },
    complete: {
      color: '#4caf50',
      iconColor: '#4caf50',
    },
  },
  hidePostalCode: false,
};

// Main checkout form component
function CheckoutForm({ listing, quantity = 1, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentSucceeded, setPaymentSucceeded] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [isCreatingPayment, setIsCreatingPayment] = useState(false);
  const user = auth.currentUser;

  const totalPrice = listing.price * quantity;
  const fees = calculateFees(totalPrice);

  const createPaymentIntent = useCallback(async () => {
      if (isCreatingPayment) {
        return;
      }
      
      try {
        setIsCreatingPayment(true);
        
        let result;
        
        // Development mode: Mock Stripe Checkout session
        if (process.env.REACT_APP_USE_EMULATORS === "true") {
          // Simulate API delay
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Mock checkout session - redirect to success page
          alert(`🧪 DEVELOPMENT MODE\n\nMock payment would process:\n• Item: ${listing.title}\n• Quantity: ${quantity}\n• Amount: $${(listing.price * quantity).toFixed(2)}\n\nIn production, this would redirect to Stripe Checkout.`);
          
          // Mock successful completion
          if (onSuccess) {
            onSuccess({
              paymentIntentId: `pi_mock_${Date.now()}`,
              amount: listing.price * quantity,
              quantity: quantity
            });
          }
          
          return;
        }
        
        // Production mode: Create actual Stripe Checkout session
        const { httpsCallable } = await import('firebase/functions');
        const { functions } = await import('../services/firebase');
        
        const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
        
        result = await createCheckoutSession({
          listingId: listing.id,
          listingTitle: listing.title,
          amount: Math.round(listing.price * quantity * 100), // Pass just item price, not total with fees
          sellerId: listing.sellerId || listing.createdBy,
          purchaseQuantity: quantity, // Pass the actual selected quantity
        });
        
        // Validate that we received a proper clientSecret
        if (!result.data.clientSecret || !result.data.clientSecret.startsWith('pi_')) {
          throw new Error('Invalid clientSecret received from server');
        }
        
        setClientSecret(result.data.clientSecret);
      } catch (err) {
        // More detailed error messages
        if (err.code === 'unauthenticated') {
          setError('Please sign in to complete your purchase.');
        } else if (err.code === 'permission-denied') {
          setError('Seller account not set up properly. Please contact support.');
        } else if (err.code === 'failed-precondition') {
          setError(err.message || 'Unable to complete purchase at this time.');
        } else if (err.message) {
          setError(err.message);
        } else {
          setError('Failed to initialize payment. Please try again or contact support.');
        }
      } finally {
        setIsCreatingPayment(false);
      }
  }, [listing?.id, listing?.createdBy, listing?.sellerId, listing?.title, quantity, listing?.price, isCreatingPayment, onSuccess]);

  useEffect(() => {
    // Clear any existing state when dependencies change
    setClientSecret('');
    setError(null);
    
    if (listing && user) {
      createPaymentIntent();
    }
  }, [listing, user, createPaymentIntent]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (!clientSecret) {
      setError('Payment not initialized. Please refresh and try again.');
      return;
    }

    if (isCreatingPayment) {
      setError('Payment is still being initialized. Please wait a moment and try again.');
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      // Create payment method
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: user.displayName || user.email,
          email: user.email,
        },
      });

      if (paymentMethodError) {
        // Check for test card in production
        if (paymentMethodError.code === 'card_declined' && 
            paymentMethodError.message?.includes('test')) {
          throw new Error('Test cards cannot be used in production. Please use a real payment card.');
        }
        throw new Error(paymentMethodError.message);
      }

    // Add a small delay to ensure PaymentIntent is fully processed
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Try to retrieve the PaymentIntent first to verify it exists
    try {
      const retrieveResult = await stripe.retrievePaymentIntent(clientSecret);
      
      if (!retrieveResult.paymentIntent) {
        throw new Error('PaymentIntent object is null or undefined');
      }
      
    } catch (retrieveError) {
      throw new Error(`PaymentIntent not found: ${retrieveError.message}`);
    }
    
    const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: paymentMethod.id
    });

      if (confirmError) {
        // Enhanced error handling for test cards and declined payments
        if (confirmError.decline_code === 'test_mode_live_card' || 
            confirmError.message?.toLowerCase().includes('test')) {
          throw new Error('Test cards cannot be used in production. Please use a real payment card.');
        }
        
        if (confirmError.code === 'card_declined') {
          throw new Error(`Card declined: ${confirmError.message || 'Please check your card details and try again.'}`);
        }
        
        throw new Error(confirmError.message);
      }

      if (paymentIntent.status === 'succeeded') {
        // Payment succeeded! Now wait for webhook to create transaction
        setPaymentSucceeded(true);
        
        // Poll Firestore to check if webhook has created the transaction
        const checkTransaction = async () => {
          try {
            const { collection: firestoreCollection, query, where, getDocs } = await import('firebase/firestore');
            
            let attempts = 0;
            const maxAttempts = 5; // Try for 5 seconds
            
            while (attempts < maxAttempts) {
              const transactionsQuery = query(
                firestoreCollection(db, 'transactions'),
                where('stripePaymentIntentId', '==', paymentIntent.id)
              );
              
              const transactionSnap = await getDocs(transactionsQuery);
              
              if (!transactionSnap.empty) {
                // Transaction created by webhook! 
                const transactionDoc = transactionSnap.docs[0];
                
                setTimeout(() => {
                  onSuccess({
                    transactionId: transactionDoc.id,
                    paymentIntent,
                    listing,
                  });
                }, 1500);
                return;
              }
              
              // Wait 1 second before trying again
              await new Promise(resolve => setTimeout(resolve, 1000));
              attempts++;
            }
            
            // Webhook didn't process in time - create transaction ourselves as fallback
            const transactionData = {
              stripePaymentIntentId: paymentIntent.id,
              listingId: listing.id,
              listingTitle: listing.title,
              itemName: listing.title,
              buyerId: user.uid,
              buyerEmail: user.email,
              sellerId: listing.sellerId || listing.createdBy,
              amount: Math.round(totalPrice * 100),
              sellerAmount: Math.round(totalPrice * 100 * 0.95),
              platformFee: Math.round(totalPrice * 100 * 0.05),
              purchaseQuantity: quantity,
              status: 'completed',
              paymentMethodId: paymentMethod.id,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            };

            const transactionRef = await addDoc(collection(db, 'transactions'), transactionData);

            await updateDoc(doc(db, 'listings', listing.id), {
              status: 'sold',
              soldAt: serverTimestamp(),
              soldTo: user.uid,
              transactionId: transactionRef.id,
            });
            
            setTimeout(() => {
              onSuccess({
                transactionId: transactionRef.id,
                paymentIntent,
                listing,
              });
            }, 1500);
            
          } catch (error) {
            // Still show success - payment went through
            setTimeout(() => {
              onSuccess({
                paymentIntent,
                listing,
              });
            }, 1500);
          }
        };
        
        checkTransaction();
      } else {
        throw new Error('Payment was not completed successfully');
      }

    } catch (err) {
      setError(err.message || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (paymentSucceeded) {
    return (
      <div className="checkout-success">
        <div className="success-animation">
          <div className="success-icon">✓</div>
          <h2>Payment Successful!</h2>
          <p>Your purchase of "{listing.title}" has been completed.</p>
          <div className="success-details">
            <div className="detail-row">
              <span>Item:</span>
              <span>{listing.title}</span>
            </div>
            <div className="detail-row">
              <span>Amount:</span>
              <span>${fees.total}</span>
            </div>
          </div>
        </div>

        <style jsx>{`
          .checkout-success {
            max-width: 480px;
            margin: 0 auto;
            text-align: center;
            padding: 48px 32px;
            background: ${COLORS.white};
            border-radius: ${LAYOUT.borderRadius.lg};
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
            border: 1px solid ${COLORS.border};
          }

          .success-animation {
            animation: fadeIn 0.5s ease-out;
          }

          .success-icon {
            width: 64px;
            height: 64px;
            background: ${COLORS.success};
            color: ${COLORS.white};
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 2rem;
            font-weight: bold;
            margin: 0 auto 24px;
            animation: scaleIn 0.5s ease-out;
          }

          .checkout-success h2 {
            color: ${COLORS.text.primary};
            margin-bottom: 12px;
            font-size: 1.5rem;
            font-weight: 600;
          }

          .checkout-success p {
            color: ${COLORS.text.secondary};
            margin-bottom: 24px;
            font-size: 0.95rem;
            line-height: 1.5;
          }

          .success-details {
            background: ${COLORS.grayLight};
            border-radius: ${LAYOUT.borderRadius.md};
            padding: 20px;
            margin-top: 24px;
            border: 1px solid ${COLORS.border};
          }

          .detail-row {
            display: flex;
            justify-content: space-between;
            padding: 12px 0;
            border-bottom: 1px solid ${COLORS.border};
          }

          .detail-row:last-child {
            border-bottom: none;
          }

          .detail-row span:first-child {
            font-weight: 500;
            color: ${COLORS.text.secondary};
          }

          .detail-row span:last-child {
            color: ${COLORS.primary};
            font-weight: 600;
          }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes scaleIn {
            from { transform: scale(0); }
            to { transform: scale(1); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      <div className="checkout-header">
        <h2 className="checkout-title">Complete Your Purchase</h2>
        <p className="checkout-subtitle">
          You're buying "{listing.title}" from the Redbird Markets
        </p>
      </div>

      <div className="payment-summary">
        <div className="summary-header">
          <h3>Payment Summary</h3>
        </div>
        <div className="summary-details">
          <div className="summary-row">
            <span>Item Price:</span>
            <span>${fees.subtotal}</span>
          </div>
          <div className="summary-row total-row">
            <span>Total:</span>
            <span>${fees.total}</span>
          </div>
        </div>
      </div>

      <div className="card-section">
        <label className="card-label">Payment Information</label>
        <div className="card-element-wrapper">
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="checkout-actions">
        <button
          type="button"
          onClick={onCancel}
          className="cancel-button"
          disabled={processing}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || processing}
          className="pay-button"
        >
          {processing ? (
            <>
              <span className="button-spinner"></span>
              Processing...
            </>
          ) : (
            `Pay $${fees.total}`
          )}
        </button>
      </div>

      <div className="security-notice">
        🔒 Your payment information is secure and encrypted
      </div>

      <style jsx>{`
        .checkout-form {
          max-width: 480px;
          margin: 0 auto;
          padding: 32px;
          background: ${COLORS.white};
          border-radius: ${LAYOUT.borderRadius.lg};
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
          border: 1px solid ${COLORS.border};
        }

        .checkout-header {
          margin-bottom: ${LAYOUT.spacing.lg};
          padding-bottom: ${LAYOUT.spacing.lg};
          border-bottom: 1px solid ${COLORS.border};
        }

        .checkout-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: ${COLORS.text.primary};
          margin: 0 0 ${LAYOUT.spacing.sm} 0;
        }

        .checkout-subtitle {
          color: ${COLORS.text.secondary};
          margin: 0;
          font-size: 0.9rem;
          line-height: 1.5;
        }

        .payment-summary {
          background: ${COLORS.grayLight};
          border: 1px solid ${COLORS.border};
          border-radius: ${LAYOUT.borderRadius.md};
          padding: ${LAYOUT.spacing.lg};
          margin-bottom: ${LAYOUT.spacing.lg};
        }

        .summary-header h3 {
          margin: 0 0 ${LAYOUT.spacing.md} 0;
          font-size: 1rem;
          font-weight: 600;
          color: ${COLORS.text.primary};
        }

        .summary-details {
          display: flex;
          flex-direction: column;
          gap: ${LAYOUT.spacing.sm};
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          padding: ${LAYOUT.spacing.sm} 0;
          font-size: 0.95rem;
          color: ${COLORS.text.secondary};
        }

        .total-row {
          font-weight: 700;
          font-size: 1.25rem;
          color: ${COLORS.primary};
          border-top: 2px solid ${COLORS.border};
          margin-top: ${LAYOUT.spacing.sm};
          padding-top: ${LAYOUT.spacing.md};
        }

        .card-section {
          margin-bottom: ${LAYOUT.spacing.lg};
        }

        .card-label {
          display: block;
          font-weight: 600;
          color: ${COLORS.text.primary};
          margin-bottom: ${LAYOUT.spacing.sm};
          font-size: 0.9rem;
        }

        .card-element-wrapper {
          background: ${COLORS.white};
          border: 2px solid ${COLORS.border};
          border-radius: ${LAYOUT.borderRadius.md};
          padding: ${LAYOUT.spacing.md};
          transition: all 0.2s ease;
        }

        .card-element-wrapper:focus-within {
          border-color: ${COLORS.primary};
          box-shadow: 0 0 0 3px rgba(206, 17, 38, 0.08);
        }

        .error-message {
          background: rgba(206, 17, 38, 0.08);
          border: 1px solid rgba(206, 17, 38, 0.2);
          border-radius: ${LAYOUT.borderRadius.md};
          padding: ${LAYOUT.spacing.md};
          margin-bottom: ${LAYOUT.spacing.md};
          color: ${COLORS.primary};
          font-size: 0.9rem;
          font-weight: 500;
        }

        .checkout-actions {
          display: flex;
          gap: ${LAYOUT.spacing.md};
          margin-bottom: ${LAYOUT.spacing.lg};
        }

        .cancel-button {
          flex: 1;
          padding: 14px 20px;
          background: ${COLORS.white};
          color: ${COLORS.text.secondary};
          border: 2px solid ${COLORS.border};
          border-radius: ${LAYOUT.borderRadius.md};
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .cancel-button:hover:not(:disabled) {
          background: ${COLORS.grayLight};
          border-color: ${COLORS.gray};
        }

        .cancel-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pay-button {
          flex: 2;
          padding: 14px 20px;
          background: ${COLORS.primary};
          color: ${COLORS.white};
          border: none;
          border-radius: ${LAYOUT.borderRadius.md};
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: ${LAYOUT.spacing.sm};
        }

        .pay-button:hover:not(:disabled) {
          background: ${COLORS.primaryDark};
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(206, 17, 38, 0.25);
        }

        .pay-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .button-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid ${COLORS.white};
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        .security-notice {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: ${LAYOUT.spacing.sm};
          font-size: 0.85rem;
          color: ${COLORS.text.muted};
          text-align: center;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .checkout-form {
            margin: 16px;
            padding: 24px;
          }
          
          .checkout-actions {
            flex-direction: column;
          }

          .cancel-button,
          .pay-button {
            flex: 1;
          }
        }
      `}</style>
    </form>
  );
}

// Main Stripe Checkout wrapper component
function StripeCheckout({ listing, quantity = 1, onSuccess, onCancel }) {
  return (
    <div className="stripe-checkout-wrapper">
      <Elements stripe={stripePromise}>
        <CheckoutForm 
          listing={listing} 
          quantity={quantity}
          onSuccess={onSuccess} 
          onCancel={onCancel} 
        />
      </Elements>

      <style jsx>{`
        .stripe-checkout-wrapper {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
          backdrop-filter: blur(5px);
        }
      `}</style>
    </div>
  );
}

export default StripeCheckout;